import React, { useState, useEffect, useCallback } from "react";
import api from "../lib/api";

const STAGES = [
  { key: "new", label: "New", color: "#6b7280", bg: "#1f2937" },
  { key: "reviewed", label: "Reviewed", color: "#3b82f6", bg: "#1e3a5f" },
  { key: "approved", label: "Approved", color: "#8b5cf6", bg: "#2d1b69" },
  { key: "contacted", label: "Contacted", color: "#f59e0b", bg: "#451a03" },
  { key: "converted", label: "Converted", color: "#10b981", bg: "#064e3b" },
];

const STAGE_NEXT = {
  new: "reviewed",
  reviewed: "approved",
  approved: "contacted",
  contacted: "converted",
};
const STAGE_PREV = {
  reviewed: "new",
  approved: "reviewed",
  contacted: "approved",
  converted: "contacted",
};

const PLATFORM_BADGE = {
  kazilink: { label: "KaziLink", color: "#10b981", bg: "#064e3b" },
  sitedesk: { label: "SiteDesk", color: "#3b82f6", bg: "#1e3a5f" },
};

function ConfidenceBadge({ score }) {
  const pct = Math.round((score || 0) * 100);
  const color = pct >= 70 ? "#10b981" : pct >= 50 ? "#f59e0b" : "#ef4444";
  return (
    <span style={{
      fontSize: "11px", fontWeight: 700, color,
      background: color + "20", borderRadius: "4px", padding: "2px 6px"
    }}>{pct}%</span>
  );
}

function PlatformBadge({ platform }) {
  const badge = PLATFORM_BADGE[platform] || { label: platform || "Manual", color: "#9ca3af", bg: "#374151" };
  return (
    <span style={{
      fontSize: "11px", fontWeight: 700, color: badge.color,
      background: badge.bg, borderRadius: "4px", padding: "2px 7px", letterSpacing: "0.5px"
    }}>{badge.label}</span>
  );
}

function StatusChip({ status }) {
  const colors = {
    draft: "#f59e0b",
    approved: "#10b981",
    rejected: "#ef4444",
    sent: "#3b82f6",
  };
  const color = colors[status] || "#9ca3af";
  return (
    <span style={{
      fontSize: "11px", fontWeight: 600, color,
      background: color + "20", borderRadius: "4px", padding: "2px 7px"
    }}>{status}</span>
  );
}

function btnStyle(bg, color, bold = false) {
  return {
    fontSize: "12px", fontWeight: bold ? 700 : 600, color, background: bg,
    border: `1px solid ${color}40`, borderRadius: "6px", padding: "5px 12px",
    cursor: "pointer", transition: "opacity 0.15s"
  };
}

function LeadCard({ lead, selected, onClick, onStageChange }) {
  const stageInfo = STAGES.find(s => s.key === lead.pipeline_stage) || STAGES[0];
  return (
    <div onClick={onClick} style={{
      background: selected ? "#1e40af22" : "#1f2937",
      border: selected ? "1.5px solid #3b82f6" : "1px solid #374151",
      borderRadius: "10px", padding: "12px 14px", cursor: "pointer",
      marginBottom: "8px", transition: "all 0.15s"
    }}>
      <div style={{ display: "flex", gap: "6px", alignItems: "center", flexWrap: "wrap", marginBottom: "4px" }}>
        <PlatformBadge platform={lead.platform} />
        <ConfidenceBadge score={lead.confidence_score} />
        {lead.is_duplicate && (
          <span style={{ fontSize: "10px", color: "#ef4444", background: "#ef444420", borderRadius: "4px", padding: "2px 5px" }}>DUP</span>
        )}
      </div>
      <div style={{ fontWeight: 600, color: "#f9fafb", fontSize: "14px", marginBottom: "2px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {lead.full_name || "Unknown"}
      </div>
      <div style={{ fontSize: "12px", color: "#9ca3af", marginBottom: "3px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {lead.business_name}
      </div>
      <div style={{ fontSize: "11px", color: "#6b7280", display: "flex", gap: "8px", flexWrap: "wrap" }}>
        {lead.location && <span>📍 {lead.location}</span>}
        {lead.category && <span>🔧 {lead.category}</span>}
      </div>
      <div style={{ display: "flex", gap: "6px", marginTop: "8px", justifyContent: "flex-end" }}>
        {STAGE_PREV[lead.pipeline_stage] && (
          <button onClick={e => { e.stopPropagation(); onStageChange(lead, STAGE_PREV[lead.pipeline_stage]); }} style={{
            fontSize: "11px", background: "#374151", color: "#9ca3af", border: "none",
            borderRadius: "5px", padding: "3px 8px", cursor: "pointer"
          }}>← Back</button>
        )}
        {STAGE_NEXT[lead.pipeline_stage] && (
          <button onClick={e => { e.stopPropagation(); onStageChange(lead, STAGE_NEXT[lead.pipeline_stage]); }} style={{
            fontSize: "11px", background: stageInfo.color + "30", color: stageInfo.color, border: "none",
            borderRadius: "5px", padding: "3px 8px", cursor: "pointer", fontWeight: 600
          }}>→ {STAGE_NEXT[lead.pipeline_stage].charAt(0).toUpperCase() + STAGE_NEXT[lead.pipeline_stage].slice(1)}</button>
        )}
      </div>
    </div>
  );
}

function LeadDetail({ lead, outreach, onStageChange, onGenerateOutreach, onApprove, onReject, onMarkSent, onEditOutreach, loading }) {
  const [editMode, setEditMode] = useState(false);
  const [editedSubject, setEditedSubject] = useState("");
  const [editedEmail, setEditedEmail] = useState("");
  const [editedWa, setEditedWa] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectInput, setShowRejectInput] = useState(false);

  const draft = outreach.find(o => o.status === "draft") || null;
  const approved = outreach.find(o => o.status === "approved") || null;
  const sent = outreach.filter(o => o.status === "sent");
  const latest = draft || approved || (outreach.length > 0 ? outreach[0] : null);

  const startEdit = () => {
    if (!draft) return;
    setEditedSubject(draft.email_subject || "");
    setEditedEmail(draft.email_body || "");
    setEditedWa(draft.whatsapp_body || "");
    setEditMode(true);
  };

  const saveEdit = () => {
    onEditOutreach(draft.outreach_id, {
      email_subject: editedSubject,
      email_body: editedEmail,
      whatsapp_body: editedWa,
    });
    setEditMode(false);
  };

  const stageInfo = STAGES.find(s => s.key === lead.pipeline_stage) || STAGES[0];

  return (
    <div style={{ height: "100%", overflowY: "auto", paddingBottom: "20px" }}>
      {/* Lead info */}
      <div style={{ background: "#1f2937", borderRadius: "12px", padding: "18px", marginBottom: "12px" }}>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "10px" }}>
          <PlatformBadge platform={lead.platform} />
          <ConfidenceBadge score={lead.confidence_score} />
          <span style={{
            fontSize: "11px", fontWeight: 700, color: stageInfo.color,
            background: stageInfo.bg, borderRadius: "4px", padding: "2px 7px"
          }}>{stageInfo.label}</span>
        </div>
        <h2 style={{ margin: "0 0 4px", color: "#f9fafb", fontSize: "18px" }}>{lead.full_name}</h2>
        <div style={{ color: "#9ca3af", fontSize: "13px", marginBottom: "12px" }}>{lead.business_name}</div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "7px", fontSize: "12px", color: "#d1d5db" }}>
          {lead.location && <div>📍 {lead.location}</div>}
          {lead.category && <div>🔧 {lead.category}</div>}
          {lead.email && <div style={{ gridColumn: "span 2" }}>✉️ {lead.email}</div>}
          {lead.phone && <div>📞 {lead.phone}</div>}
          {lead.website && <div style={{ gridColumn: "span 2" }}>🌐 <a href={lead.website} target="_blank" rel="noreferrer" style={{ color: "#60a5fa" }}>{lead.website}</a></div>}
          {lead.enrichment_data?.rating && (
            <div>⭐ {lead.enrichment_data.rating} ({lead.enrichment_data.reviews_count || 0} reviews)</div>
          )}
          {lead.enrichment_data?.years_active && (
            <div>🏢 {lead.enrichment_data.years_active} yrs</div>
          )}
          {lead.enrichment_data?.abn && <div style={{ gridColumn: "span 2" }}>ABN: {lead.enrichment_data.abn}</div>}
          {lead.enrichment_data?.hourly_rate_aud && (
            <div>💰 ~A${lead.enrichment_data.hourly_rate_aud}/hr</div>
          )}
        </div>

        {lead.notes && (
          <div style={{ marginTop: "10px", fontSize: "12px", color: "#9ca3af", background: "#374151", borderRadius: "6px", padding: "8px 10px" }}>
            {lead.notes}
          </div>
        )}

        <div style={{ marginTop: "10px", fontSize: "11px", color: "#6b7280" }}>
          Source: {lead.source || "manual"}
          {lead.source_url && <> · <a href={lead.source_url} target="_blank" rel="noreferrer" style={{ color: "#60a5fa" }}>View listing ↗</a></>}
        </div>
      </div>

      {/* Pipeline stage controls */}
      <div style={{ background: "#1f2937", borderRadius: "12px", padding: "14px", marginBottom: "12px" }}>
        <div style={{ fontSize: "11px", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "10px" }}>
          Move Stage
        </div>
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
          {STAGES.map(stage => (
            <button key={stage.key}
              onClick={() => onStageChange(lead, stage.key)}
              style={{
                fontSize: "12px", fontWeight: lead.pipeline_stage === stage.key ? 700 : 500,
                color: lead.pipeline_stage === stage.key ? stage.color : "#6b7280",
                background: lead.pipeline_stage === stage.key ? stage.bg : "#374151",
                border: lead.pipeline_stage === stage.key ? `1.5px solid ${stage.color}` : "1px solid #4b5563",
                borderRadius: "6px", padding: "5px 10px", cursor: "pointer", transition: "all 0.15s"
              }}
            >{stage.label}</button>
          ))}
        </div>
      </div>

      {/* Outreach section */}
      <div style={{ background: "#1f2937", borderRadius: "12px", padding: "14px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
          <div style={{ fontSize: "11px", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.8px" }}>
            Outreach
          </div>
          <button
            onClick={() => onGenerateOutreach(lead.lead_id)}
            disabled={loading}
            style={btnStyle("#1e1b4b", "#818cf8")}
          >
            {loading ? "Generating…" : "+ Generate Draft"}
          </button>
        </div>

        {outreach.length === 0 && (
          <div style={{ textAlign: "center", color: "#6b7280", fontSize: "13px", padding: "12px 0" }}>
            No outreach yet. Generate a personalized draft to get started.
          </div>
        )}

        {latest && !editMode && (
          <div style={{ background: "#111827", borderRadius: "8px", padding: "12px", marginBottom: "8px" }}>
            <div style={{ display: "flex", gap: "8px", marginBottom: "10px", alignItems: "center", flexWrap: "wrap" }}>
              <StatusChip status={latest.status} />
              <span style={{ fontSize: "11px", color: "#6b7280" }}>via {latest.channel}</span>
              {latest.approved_by && <span style={{ fontSize: "11px", color: "#10b981" }}>✓ approved</span>}
              {latest.sent_at && <span style={{ fontSize: "11px", color: "#60a5fa" }}>sent {new Date(latest.sent_at).toLocaleDateString()}</span>}
            </div>

            {latest.email_subject && (
              <div style={{ marginBottom: "8px" }}>
                <div style={{ fontSize: "10px", color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: "4px" }}>Email Subject</div>
                <div style={{ fontSize: "13px", fontWeight: 600, color: "#e2e8f0" }}>{latest.email_subject}</div>
              </div>
            )}

            <div style={{ marginBottom: "10px" }}>
              <div style={{ fontSize: "10px", color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: "4px" }}>Email Body</div>
              <div style={{ fontSize: "12px", color: "#d1d5db", background: "#1f2937", borderRadius: "6px", padding: "10px", whiteSpace: "pre-wrap", maxHeight: "200px", overflowY: "auto", lineHeight: 1.5 }}>
                {latest.email_body}
              </div>
            </div>

            {latest.whatsapp_body && (
              <div style={{ marginBottom: "10px" }}>
                <div style={{ fontSize: "10px", color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: "4px" }}>WhatsApp</div>
                <div style={{ fontSize: "12px", color: "#d1d5db", background: "#1f2937", borderRadius: "6px", padding: "10px", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                  {latest.whatsapp_body}
                </div>
              </div>
            )}

            {latest.rejection_reason && (
              <div style={{ fontSize: "11px", color: "#ef4444", background: "#ef444420", borderRadius: "5px", padding: "6px 10px", marginBottom: "8px" }}>
                Rejected: {latest.rejection_reason}
              </div>
            )}

            {latest.requires_approval && latest.status === "draft" && (
              <div style={{ fontSize: "11px", color: "#f59e0b", background: "#451a0320", border: "1px solid #f59e0b30", borderRadius: "5px", padding: "6px 10px", marginBottom: "10px" }}>
                🔒 Requires admin approval before sending. No auto-send.
              </div>
            )}

            <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
              {latest.status === "draft" && (
                <>
                  <button onClick={startEdit} style={btnStyle("#374151", "#9ca3af")}>Edit</button>
                  <button onClick={() => onApprove(latest.outreach_id)} style={btnStyle("#065f46", "#10b981")}>✓ Approve</button>
                  <button onClick={() => setShowRejectInput(!showRejectInput)} style={btnStyle("#7f1d1d", "#ef4444")}>✗ Reject</button>
                </>
              )}
              {latest.status === "approved" && (
                <button onClick={() => onMarkSent(latest.outreach_id)} style={btnStyle("#1e3a5f", "#3b82f6", true)}>
                  📨 Mark as Sent (Manual)
                </button>
              )}
              {(latest.status === "rejected" || latest.status === "sent") && (
                <button onClick={() => onGenerateOutreach(lead.lead_id, true)} style={btnStyle("#1e1b4b", "#818cf8")}>
                  + New Draft
                </button>
              )}
            </div>

            {showRejectInput && (
              <div style={{ marginTop: "8px" }}>
                <input
                  type="text"
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  placeholder="Reason (optional)"
                  style={{ width: "100%", background: "#111827", border: "1px solid #374151", borderRadius: "6px", color: "#f9fafb", padding: "7px 10px", fontSize: "12px", boxSizing: "border-box" }}
                />
                <div style={{ display: "flex", gap: "6px", marginTop: "6px" }}>
                  <button onClick={() => { onReject(latest.outreach_id, rejectReason); setShowRejectInput(false); setRejectReason(""); }} style={btnStyle("#7f1d1d", "#ef4444")}>Confirm Reject</button>
                  <button onClick={() => setShowRejectInput(false)} style={btnStyle("#374151", "#9ca3af")}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}

        {editMode && draft && (
          <div style={{ background: "#111827", borderRadius: "8px", padding: "12px" }}>
            <div style={{ marginBottom: "8px" }}>
              <div style={{ fontSize: "10px", color: "#6b7280", marginBottom: "4px" }}>Subject</div>
              <input type="text" value={editedSubject} onChange={e => setEditedSubject(e.target.value)}
                style={{ width: "100%", background: "#1f2937", border: "1px solid #374151", borderRadius: "6px", color: "#f9fafb", padding: "7px 10px", fontSize: "13px", boxSizing: "border-box" }} />
            </div>
            <div style={{ marginBottom: "8px" }}>
              <div style={{ fontSize: "10px", color: "#6b7280", marginBottom: "4px" }}>Email Body</div>
              <textarea value={editedEmail} onChange={e => setEditedEmail(e.target.value)} rows={10}
                style={{ width: "100%", background: "#1f2937", border: "1px solid #374151", borderRadius: "6px", color: "#f9fafb", padding: "8px 10px", fontSize: "12px", boxSizing: "border-box", lineHeight: 1.5, resize: "vertical" }} />
            </div>
            <div style={{ marginBottom: "12px" }}>
              <div style={{ fontSize: "10px", color: "#6b7280", marginBottom: "4px" }}>WhatsApp</div>
              <textarea value={editedWa} onChange={e => setEditedWa(e.target.value)} rows={4}
                style={{ width: "100%", background: "#1f2937", border: "1px solid #374151", borderRadius: "6px", color: "#f9fafb", padding: "8px 10px", fontSize: "12px", boxSizing: "border-box", lineHeight: 1.5, resize: "vertical" }} />
            </div>
            <div style={{ display: "flex", gap: "6px" }}>
              <button onClick={saveEdit} style={btnStyle("#065f46", "#10b981")}>Save</button>
              <button onClick={() => setEditMode(false)} style={btnStyle("#374151", "#9ca3af")}>Cancel</button>
            </div>
          </div>
        )}

        {sent.length > 0 && (
          <div style={{ marginTop: "8px", fontSize: "11px", color: "#6b7280" }}>
            {sent.length} message{sent.length > 1 ? "s" : ""} sent to this lead.
          </div>
        )}
      </div>
    </div>
  );
}

function StatChip({ label, value, color }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "5px", background: "#1f2937", borderRadius: "6px", padding: "4px 10px" }}>
      <span style={{ fontSize: "11px", color: "#6b7280" }}>{label}</span>
      <span style={{ fontSize: "13px", fontWeight: 700, color }}>{value}</span>
    </div>
  );
}

export default function Leads({ projectId: propProjectId }) {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [outreachMap, setOutreachMap] = useState({});
  const [selectedLead, setSelectedLead] = useState(null);
  const [filterStage, setFilterStage] = useState("all");
  const [filterPlatform, setFilterPlatform] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [researchLoading, setResearchLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [showResearchPanel, setShowResearchPanel] = useState(false);
  const [researchForm, setResearchForm] = useState({ platform: "kazilink", count: 30 });
  const [error, setError] = useState(null);

  // Accept projectId from prop or try to pull from URL/storage
  const [projectId, setProjectId] = useState(propProjectId || null);

  useEffect(() => {
    if (!projectId) {
      api.get("/projects/").then(res => {
        const projs = res.data || [];
        if (projs.length > 0) setProjectId(projs[0].project_id);
      }).catch(() => {});
    }
  }, [projectId]);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const loadLeads = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await api.get(`/leads/?project_id=${projectId}&limit=200`);
      setLeads(res.data || []);
      setError(null);
    } catch {
      setError("Failed to load leads. Backend may still be starting.");
    }
  }, [projectId]);

  const loadStats = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await api.get(`/leads/stats?project_id=${projectId}`);
      setStats(res.data);
    } catch { }
  }, [projectId]);

  const loadOutreachForLead = useCallback(async (leadId) => {
    try {
      const res = await api.get(`/leads/${leadId}/outreach`);
      setOutreachMap(prev => ({ ...prev, [leadId]: res.data || [] }));
    } catch { }
  }, []);

  useEffect(() => { loadLeads(); loadStats(); }, [loadLeads, loadStats]);
  useEffect(() => { if (selectedLead) loadOutreachForLead(selectedLead.lead_id); }, [selectedLead, loadOutreachForLead]);

  const handleStageChange = async (lead, newStage) => {
    try {
      await api.put(`/leads/${lead.lead_id}/stage`, { stage: newStage });
      showToast(`Moved ${lead.full_name} to ${newStage}`);
      setLeads(prev => prev.map(l => l.lead_id === lead.lead_id ? { ...l, pipeline_stage: newStage } : l));
      if (selectedLead?.lead_id === lead.lead_id) setSelectedLead(prev => ({ ...prev, pipeline_stage: newStage }));
      loadStats();
    } catch { showToast("Failed to update stage", "error"); }
  };

  const handleGenerateOutreach = async (leadId, forceNew = false) => {
    setLoading(true);
    try {
      const res = await api.post(`/leads/${leadId}/generate-outreach`, { force_new: forceNew });
      showToast(res.data.is_existing ? "Existing draft loaded" : "Draft generated — review before approving");
      await loadOutreachForLead(leadId);
    } catch { showToast("Failed to generate outreach", "error"); }
    finally { setLoading(false); }
  };

  const handleApprove = async (outreachId) => {
    try {
      await api.post(`/leads/outreach/${outreachId}/approve`);
      showToast("Approved — can now be marked as sent");
      if (selectedLead) await loadOutreachForLead(selectedLead.lead_id);
    } catch (e) { showToast(e?.response?.data?.detail || "Failed to approve", "error"); }
  };

  const handleReject = async (outreachId, reason) => {
    try {
      await api.post(`/leads/outreach/${outreachId}/reject`, { reason });
      showToast("Message rejected");
      if (selectedLead) await loadOutreachForLead(selectedLead.lead_id);
    } catch { showToast("Failed to reject", "error"); }
  };

  const handleMarkSent = async (outreachId) => {
    if (!window.confirm("Confirm: you have manually sent this message and want to record it as sent?")) return;
    try {
      await api.post(`/leads/outreach/${outreachId}/mark-sent`, {});
      showToast("Marked as sent — lead moved to Contacted");
      if (selectedLead) {
        await loadOutreachForLead(selectedLead.lead_id);
        setSelectedLead(prev => prev ? { ...prev, pipeline_stage: "contacted" } : prev);
        setLeads(prev => prev.map(l => l.lead_id === selectedLead.lead_id ? { ...l, pipeline_stage: "contacted" } : l));
        loadStats();
      }
    } catch (e) { showToast(e?.response?.data?.detail || "Failed", "error"); }
  };

  const handleEditOutreach = async (outreachId, updates) => {
    try {
      await api.put(`/leads/outreach/${outreachId}`, updates);
      showToast("Message updated");
      if (selectedLead) await loadOutreachForLead(selectedLead.lead_id);
    } catch { showToast("Failed to update", "error"); }
  };

  const handleResearch = async () => {
    if (!projectId) return;
    setResearchLoading(true);
    try {
      const res = await api.post("/leads/research", {
        project_id: projectId,
        platform: researchForm.platform,
        count: parseInt(researchForm.count),
      });
      showToast(`${res.data.saved} leads saved from ${researchForm.platform.toUpperCase()}`);
      setShowResearchPanel(false);
      await loadLeads(); await loadStats();
    } catch (e) { showToast(e?.response?.data?.detail || "Research failed", "error"); }
    finally { setResearchLoading(false); }
  };

  const handleGenerateSample = async () => {
    if (!projectId) return;
    setResearchLoading(true);
    try {
      const res = await api.post("/leads/generate-sample", { project_id: projectId });
      showToast(res.data.message);
      await loadLeads(); await loadStats();
    } catch { showToast("Failed to generate sample leads", "error"); }
    finally { setResearchLoading(false); }
  };

  const filteredLeads = leads.filter(l => {
    if (filterStage !== "all" && l.pipeline_stage !== filterStage) return false;
    if (filterPlatform !== "all" && l.platform !== filterPlatform) return false;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      if (!((l.full_name || "").toLowerCase().includes(q) ||
        (l.business_name || "").toLowerCase().includes(q) ||
        (l.location || "").toLowerCase().includes(q) ||
        (l.category || "").toLowerCase().includes(q))) return false;
    }
    return true;
  });

  const byStage = {};
  STAGES.forEach(s => { byStage[s.key] = filteredLeads.filter(l => l.pipeline_stage === s.key); });

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "#111827", color: "#f9fafb", overflow: "hidden" }}>
      {toast && (
        <div style={{
          position: "fixed", top: "20px", right: "20px", zIndex: 9999,
          background: toast.type === "error" ? "#7f1d1d" : "#065f46",
          color: toast.type === "error" ? "#fca5a5" : "#6ee7b7",
          padding: "10px 18px", borderRadius: "8px", fontSize: "13px", fontWeight: 600,
          boxShadow: "0 4px 12px rgba(0,0,0,0.4)", maxWidth: "360px"
        }}>{toast.msg}</div>
      )}

      {/* Header */}
      <div style={{ padding: "16px 20px 0", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", flexShrink: 0 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: "22px", fontWeight: 700, color: "#f9fafb" }}>Lead Pipeline</h1>
          <div style={{ fontSize: "13px", color: "#6b7280", marginTop: "2px" }}>
            Growth system — KaziLink Kenya & SiteDesk Perth/WA
          </div>
        </div>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <button onClick={handleGenerateSample} disabled={researchLoading}
            style={{ ...btnStyle("#1e1b4b", "#818cf8"), fontSize: "13px", padding: "7px 14px" }}>
            {researchLoading ? "Loading…" : "🎲 Sample Data"}
          </button>
          <button onClick={() => setShowResearchPanel(!showResearchPanel)}
            style={{ ...btnStyle("#065f46", "#10b981"), fontSize: "13px", padding: "7px 14px" }}>
            🔍 Research Leads
          </button>
          <button onClick={() => { loadLeads(); loadStats(); }} style={{ ...btnStyle("#374151", "#9ca3af"), fontSize: "13px", padding: "7px 12px" }}>
            ↻ Refresh
          </button>
        </div>
      </div>

      {/* Research panel */}
      {showResearchPanel && (
        <div style={{ margin: "12px 20px 0", background: "#1f2937", borderRadius: "10px", padding: "16px", flexShrink: 0 }}>
          <div style={{ fontSize: "13px", fontWeight: 700, color: "#f9fafb", marginBottom: "12px" }}>Lead Research Agent</div>
          <div style={{ display: "flex", gap: "10px", alignItems: "flex-end", flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: "11px", color: "#9ca3af", marginBottom: "4px" }}>Platform</div>
              <select value={researchForm.platform} onChange={e => setResearchForm(f => ({ ...f, platform: e.target.value }))}
                style={{ background: "#374151", border: "1px solid #4b5563", color: "#f9fafb", borderRadius: "6px", padding: "6px 10px", fontSize: "13px" }}>
                <option value="kazilink">KaziLink (Kenya)</option>
                <option value="sitedesk">SiteDesk (Perth/WA)</option>
              </select>
            </div>
            <div>
              <div style={{ fontSize: "11px", color: "#9ca3af", marginBottom: "4px" }}>Count</div>
              <select value={researchForm.count} onChange={e => setResearchForm(f => ({ ...f, count: e.target.value }))}
                style={{ background: "#374151", border: "1px solid #4b5563", color: "#f9fafb", borderRadius: "6px", padding: "6px 10px", fontSize: "13px" }}>
                {[10, 20, 30, 50, 100].map(n => <option key={n} value={n}>{n} leads</option>)}
              </select>
            </div>
            <button onClick={handleResearch} disabled={researchLoading} style={{ ...btnStyle("#065f46", "#10b981"), fontSize: "13px", padding: "7px 16px" }}>
              {researchLoading ? "Researching…" : "Run Research Agent"}
            </button>
            <button onClick={() => setShowResearchPanel(false)} style={{ ...btnStyle("#374151", "#9ca3af"), fontSize: "13px", padding: "7px 12px" }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Stats bar */}
      {stats && (
        <div style={{ display: "flex", gap: "8px", padding: "10px 20px", flexWrap: "wrap", flexShrink: 0 }}>
          <StatChip label="Total" value={stats.total} color="#9ca3af" />
          {STAGES.map(s => stats.by_stage?.[s.key] > 0 ?
            <StatChip key={s.key} label={s.label} value={stats.by_stage[s.key]} color={s.color} /> : null)}
          {(stats.by_platform?.kazilink || 0) > 0 && <StatChip label="KaziLink" value={stats.by_platform.kazilink} color="#10b981" />}
          {(stats.by_platform?.sitedesk || 0) > 0 && <StatChip label="SiteDesk" value={stats.by_platform.sitedesk} color="#3b82f6" />}
          {(stats.high_confidence || 0) > 0 && <StatChip label="High Conf." value={stats.high_confidence} color="#f59e0b" />}
          {(stats.outreach?.draft || 0) > 0 && <StatChip label="Drafts" value={stats.outreach.draft} color="#818cf8" />}
          {(stats.outreach?.approved || 0) > 0 && <StatChip label="Approved" value={stats.outreach.approved} color="#10b981" />}
          {(stats.outreach?.sent || 0) > 0 && <StatChip label="Sent" value={stats.outreach.sent} color="#60a5fa" />}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: "flex", gap: "8px", padding: "0 20px 10px", flexWrap: "wrap", alignItems: "center", flexShrink: 0 }}>
        <input type="text" placeholder="Search leads…" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
          style={{ flex: "1 1 180px", background: "#1f2937", border: "1px solid #374151", color: "#f9fafb", borderRadius: "7px", padding: "7px 12px", fontSize: "13px", minWidth: "120px" }} />
        <select value={filterStage} onChange={e => setFilterStage(e.target.value)}
          style={{ background: "#1f2937", border: "1px solid #374151", color: "#f9fafb", borderRadius: "7px", padding: "7px 10px", fontSize: "13px" }}>
          <option value="all">All Stages</option>
          {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <select value={filterPlatform} onChange={e => setFilterPlatform(e.target.value)}
          style={{ background: "#1f2937", border: "1px solid #374151", color: "#f9fafb", borderRadius: "7px", padding: "7px 10px", fontSize: "13px" }}>
          <option value="all">All Platforms</option>
          <option value="kazilink">KaziLink</option>
          <option value="sitedesk">SiteDesk</option>
        </select>
        <span style={{ fontSize: "12px", color: "#6b7280" }}>{filteredLeads.length} leads</span>
      </div>

      {error && (
        <div style={{ margin: "0 20px 8px", background: "#7f1d1d", color: "#fca5a5", borderRadius: "8px", padding: "10px 14px", fontSize: "13px", flexShrink: 0 }}>
          {error}
        </div>
      )}

      {leads.length === 0 && !error && (
        <div style={{ textAlign: "center", padding: "40px 20px", color: "#6b7280" }}>
          <div style={{ fontSize: "40px", marginBottom: "12px" }}>🔍</div>
          <div style={{ fontSize: "15px", fontWeight: 600, color: "#9ca3af", marginBottom: "6px" }}>No leads yet</div>
          <div style={{ fontSize: "13px" }}>Click "Sample Data" to populate demo leads, or use "Research Leads" to generate real ones.</div>
        </div>
      )}

      {/* Main layout */}
      <div style={{ flex: 1, display: "flex", gap: "0", overflow: "hidden", minHeight: 0 }}>
        {/* Kanban */}
        <div style={{ flex: 1, display: "flex", gap: "8px", overflowX: "auto", padding: "0 20px 16px", minWidth: 0 }}>
          {STAGES.map(stage => (
            <div key={stage.key} style={{ flex: "0 0 230px", display: "flex", flexDirection: "column", minWidth: "190px" }}>
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "7px 10px", borderRadius: "8px 8px 0 0",
                background: stage.bg, borderBottom: `2px solid ${stage.color}30`, marginBottom: "8px", flexShrink: 0
              }}>
                <span style={{ fontWeight: 700, fontSize: "12px", color: stage.color, textTransform: "uppercase", letterSpacing: "0.7px" }}>
                  {stage.label}
                </span>
                <span style={{ fontSize: "12px", fontWeight: 700, color: stage.color, background: stage.color + "30", borderRadius: "10px", padding: "1px 8px" }}>
                  {byStage[stage.key]?.length || 0}
                </span>
              </div>
              <div style={{ flex: 1, overflowY: "auto", paddingRight: "2px" }}>
                {(byStage[stage.key] || []).map(lead => (
                  <LeadCard
                    key={lead.lead_id}
                    lead={lead}
                    selected={selectedLead?.lead_id === lead.lead_id}
                    onClick={() => setSelectedLead(selectedLead?.lead_id === lead.lead_id ? null : lead)}
                    onStageChange={handleStageChange}
                  />
                ))}
                {(byStage[stage.key] || []).length === 0 && (
                  <div style={{ textAlign: "center", color: "#374151", fontSize: "12px", padding: "20px 8px" }}>—</div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Detail panel */}
        {selectedLead && (
          <div style={{
            flex: "0 0 370px", borderLeft: "1px solid #1f2937", overflowY: "auto",
            padding: "0 16px 16px", minWidth: "300px", maxWidth: "420px"
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 0 8px", flexShrink: 0, position: "sticky", top: 0, background: "#111827", zIndex: 10 }}>
              <div style={{ fontSize: "12px", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.8px" }}>Lead Detail</div>
              <button onClick={() => setSelectedLead(null)} style={{ background: "none", border: "none", color: "#6b7280", cursor: "pointer", fontSize: "18px", lineHeight: 1 }}>✕</button>
            </div>
            <LeadDetail
              lead={selectedLead}
              outreach={outreachMap[selectedLead.lead_id] || []}
              onStageChange={handleStageChange}
              onGenerateOutreach={handleGenerateOutreach}
              onApprove={handleApprove}
              onReject={handleReject}
              onMarkSent={handleMarkSent}
              onEditOutreach={handleEditOutreach}
              loading={loading}
            />
          </div>
        )}
      </div>
    </div>
  );
}
